import React, { useEffect, useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ActivityIndicator,
  TextInput,
  TouchableOpacity,
  ScrollView,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';

const API_BASE = 'https://collabbay.com/APIControllers';

const RedeemVoucher = ({ route }) => {
  const VoucherID = route?.params?.VoucherID;

  const [loading, setLoading] = useState(true);
  const [voucher, setVoucher] = useState(null);
  const [step, setStep] = useState('start'); // start | code | redeemed
  const [VoucherPasscode, setVoucherPasscode] = useState('');
  const [redeeming, setRedeeming] = useState(false);
  const [message, setMessage] = useState('');
  const [redeemedTime, setRedeemedTime] = useState('');

  useEffect(() => {
    fetchVoucher();
  }, [VoucherID]);

  const fetchVoucher = async () => {
    try {
      setLoading(true);

      const response = await fetch(
        `${API_BASE}/FetchRedeemVoucherInformation.php?VoucherID=${encodeURIComponent(VoucherID)}`
      );

      const data = await response.json();

      if (data.success) {
        setVoucher(data.voucher);

        if (data.voucher.AlreadyRedeemed === 'Y') {
          setStep('redeemed');
          setRedeemedTime(data.voucher.DateTimeRedeemed || '');
        }
      } else {
        setMessage(data.message || 'Voucher not found.');
      }
    } catch (error) {
      setMessage('Could not load voucher.');
    } finally {
      setLoading(false);
    }
  };

  const redeemVoucher = async () => {
    setMessage('');

    if (VoucherPasscode.length !== 4) {
      setMessage('Enter the 4-digit passcode from your email.');
      return;
    }

    try {
      setRedeeming(true);

      const response = await fetch(`${API_BASE}/CustomerRedeemVoucherInsert.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ VoucherID, VoucherPasscode }),
      });

      const data = await response.json();

      if (data.success) {
        setStep('redeemed');
        setRedeemedTime(data.redeem?.DateTimeRedeemed || '');
      } else {
        setMessage(data.message || 'Could not redeem voucher.');
      }
    } catch (error) {
      setMessage('Something went wrong redeeming this voucher.');
    } finally {
      setRedeeming(false);
    }
  };

  if (loading) {
    return (
      <View style={styles.center}>
        <ActivityIndicator size="large" color="#000" />
        <Text style={styles.loadingText}>Loading voucher...</Text>
      </View>
    );
  }

  if (!voucher) {
    return (
      <View style={styles.center}>
        <Text style={styles.errorText}>{message || 'Voucher not found.'}</Text>
      </View>
    );
  }

  return (
    <ScrollView style={styles.page} contentContainerStyle={styles.content}>
      <View style={styles.voucher}>
        <View style={styles.top}>
          <Text style={styles.small}>COLLABBAY VOUCHER</Text>
          <Text style={styles.partner}>{voucher.Promoter} × {voucher.BrandName}</Text>

          <View style={styles.line} />

          <Text style={styles.discount}>{voucher.Discount}% OFF</Text>
          <Text style={styles.spend}>when you spend £{voucher.OnSpend}</Text>

          <Text selectable style={styles.voucherId}>{voucher.VoucherID}</Text>
        </View>

        <View style={styles.bottom}>
          {step === 'start' && (
            <>
              <Text style={styles.status}>Unused voucher</Text>

              <TouchableOpacity style={styles.blackButton} onPress={() => setStep('code')}>
                <Text style={styles.blackButtonText}>Redeem voucher</Text>
              </TouchableOpacity>
            </>
          )}

          {step === 'code' && (
            <>
              <Text style={styles.title}>Enter passcode</Text>
              <Text style={styles.note}>
                Only redeem this when you are on-site and ready to show staff.
              </Text>

              <TextInput
                style={styles.input}
                value={VoucherPasscode}
                onChangeText={(text) =>
                  setVoucherPasscode(text.replace(/[^0-9]/g, '').slice(0, 4))
                }
                placeholder="0000"
                keyboardType="numeric"
                maxLength={4}
                placeholderTextColor="#aaa"
              />

              {message ? <Text style={styles.message}>{message}</Text> : null}

              <TouchableOpacity
                style={[styles.blackButton, redeeming && styles.disabled]}
                onPress={redeemVoucher}
                disabled={redeeming}
              >
                {redeeming ? (
                  <ActivityIndicator color="#fff" />
                ) : (
                  <Text style={styles.blackButtonText}>Confirm redeem</Text>
                )}
              </TouchableOpacity>

              <TouchableOpacity style={styles.textButton} onPress={() => setStep('start')}>
                <Text style={styles.textButtonText}>Cancel</Text>
              </TouchableOpacity>
            </>
          )}

          {step === 'redeemed' && (
            <View style={styles.redeemedBox}>
              <View style={styles.tick}>
                <Ionicons name="checkmark" size={46} color="#fff" />
              </View>

              <Text style={styles.redeemedTitle}>Voucher redeemed</Text>
              <Text style={styles.redeemedSub}>Show this to venue staff</Text>

              <View style={styles.greenStamp}>
                <Text style={styles.greenStampText}>ACTIVE / REDEEMED</Text>
              </View>

              <Text style={styles.timeLabel}>Redeemed at</Text>
              <Text selectable style={styles.timeValue}>{redeemedTime || 'Just now'}</Text>
            </View>
          )}
        </View>
      </View>

      <View style={styles.details}>
        <Text style={styles.detailLine}>Venue: {voucher.BrandName}</Text>
        <Text style={styles.detailLine}>Creator: {voucher.Promoter}</Text>
        <Text style={styles.detailLine}>Paid: £{voucher.AmountPaid}</Text>
      </View>
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  page: { flex: 1, backgroundColor: '#f3f3f3' },
  content: { padding: 18, paddingBottom: 60, maxWidth: 560, width: '100%', alignSelf: 'center' },
  center: { flex: 1, justifyContent: 'center', alignItems: 'center', backgroundColor: '#fff' },
  loadingText: { marginTop: 10, color: '#666' },
  errorText: { fontSize: 16, fontWeight: '800', color: '#111' },

  voucher: {
    backgroundColor: '#fff',
    borderRadius: 30,
    overflow: 'hidden',
    borderWidth: 1,
    borderColor: '#ddd',
  },
  top: {
    backgroundColor: '#111',
    padding: 28,
    alignItems: 'center',
  },
  small: {
    color: '#FDF05D',
    fontSize: 12,
    fontWeight: '900',
    letterSpacing: 2,
    marginBottom: 14,
  },
  partner: {
    color: '#fff',
    fontSize: 16,
    fontWeight: '900',
    textAlign: 'center',
  },
  line: {
    width: '100%',
    borderTopWidth: 1,
    borderStyle: 'dashed',
    borderColor: '#555',
    marginVertical: 22,
  },
  discount: {
    color: '#fff',
    fontSize: 48,
    fontWeight: '900',
  },
  spend: {
    color: '#eee',
    fontSize: 20,
    fontWeight: '800',
    marginTop: 4,
  },
  voucherId: {
    marginTop: 20,
    color: '#fff',
    backgroundColor: '#222',
    paddingVertical: 8,
    paddingHorizontal: 14,
    borderRadius: 999,
    fontSize: 13,
    fontWeight: '900',
    letterSpacing: 1,
  },
  bottom: {
    padding: 22,
    alignItems: 'center',
  },
  status: {
    backgroundColor: '#fff4cc',
    color: '#8a6400',
    fontWeight: '900',
    paddingVertical: 8,
    paddingHorizontal: 14,
    borderRadius: 999,
    marginBottom: 18,
  },
  blackButton: {
    backgroundColor: '#111',
    borderRadius: 999,
    paddingVertical: 15,
    paddingHorizontal: 24,
    width: '100%',
    alignItems: 'center',
  },
  blackButtonText: {
    color: '#fff',
    fontSize: 15,
    fontWeight: '900',
  },
  title: {
    fontSize: 24,
    fontWeight: '900',
    color: '#111',
    marginBottom: 8,
  },
  note: {
    textAlign: 'center',
    color: '#666',
    fontSize: 14,
    lineHeight: 20,
    marginBottom: 18,
  },
  input: {
    width: '100%',
    borderWidth: 1,
    borderColor: '#ddd',
    borderRadius: 18,
    paddingVertical: 15,
    paddingHorizontal: 16,
    textAlign: 'center',
    fontSize: 30,
    fontWeight: '900',
    letterSpacing: 10,
    marginBottom: 12,
    outlineStyle: 'none',
  },
  message: {
    color: '#b00020',
    fontWeight: '800',
    textAlign: 'center',
    marginBottom: 12,
  },
  disabled: { opacity: 0.6 },
  textButton: { marginTop: 14 },
  textButtonText: { color: '#777', fontWeight: '800' },

  redeemedBox: { alignItems: 'center', width: '100%' },
  tick: {
    width: 86,
    height: 86,
    borderRadius: 43,
    backgroundColor: '#0a7d28',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 14,
  },
  redeemedTitle: {
    fontSize: 28,
    fontWeight: '900',
    color: '#0a7d28',
  },
  redeemedSub: {
    color: '#276b39',
    fontWeight: '800',
    marginTop: 5,
  },
  greenStamp: {
    marginTop: 18,
    borderWidth: 2,
    borderColor: '#0a7d28',
    borderRadius: 12,
    paddingVertical: 8,
    paddingHorizontal: 14,
  },
  greenStampText: {
    color: '#0a7d28',
    fontWeight: '900',
    letterSpacing: 1,
  },
  timeLabel: {
    marginTop: 18,
    color: '#777',
    fontSize: 12,
    fontWeight: '900',
  },
  timeValue: {
    marginTop: 5,
    color: '#111',
    fontSize: 15,
    fontWeight: '900',
  },
  details: {
    marginTop: 16,
    backgroundColor: '#fff',
    borderRadius: 20,
    padding: 16,
    borderWidth: 1,
    borderColor: '#e5e5e5',
  },
  detailLine: {
    fontSize: 13,
    color: '#333',
    fontWeight: '800',
    marginBottom: 6,
  },
});

export default RedeemVoucher;